import React, { useState, useEffect } from 'react';
import { Menu, X, Phone, Mail, MapPin, ChevronRight, CheckCircle, Lightbulb, TrendingUp, Users, Image, MessageSquare, Clipboard } from 'lucide-react';

// Main App Component
const App = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // Smooth scroll function
  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setIsMobileMenuOpen(false); // Close menu after clicking on mobile
    }
  };

  // Effect to handle body scroll lock when mobile menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = ''; // Cleanup on unmount
    };
  }, [isMobileMenuOpen]);

  return (
    <div className="font-inter antialiased text-gray-800 bg-white">
      {/* Navbar */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white shadow-md py-4 px-6 md:px-12">
        <div className="container mx-auto flex justify-between items-center">
          <div className="flex items-center">
            <img
  src="https://i.ibb.co/m3C5c7N/us-akshita-logo-01.png"
  alt="Unitech Systems Logo"
  className="h-16 w-16 md:h-20 md:w-20 rounded-full mr-4"
/>
            <a href="#home" onClick={() => scrollToSection('home')} className="text-2xl font-bold text-blue-700 hover:text-blue-800 transition-colors duration-300">Unitech Systems</a>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex space-x-8">
            <NavLink id="home" text="Home" onClick={scrollToSection} />
            <NavLink id="about" text="About Us" onClick={scrollToSection} />
            <NavLink id="services" text="Services" onClick={scrollToSection} />
            <NavLink id="products" text="Products" onClick={scrollToSection} />
            <NavLink id="gallery" text="Gallery" onClick={scrollToSection} />
            <NavLink id="testimonials" text="Testimonials" onClick={scrollToSection} />
            <NavLink id="contact" text="Contact" onClick={scrollToSection} />
            <NavLink id="interest-form" text="Interest Form" onClick={scrollToSection} />
          </div>

          {/* Mobile Menu Button */}
          <div className="md:hidden">
            <button onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} className="text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 rounded-md p-2">
              {isMobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
            </button>
          </div>
        </div>

        {/* Mobile Menu Overlay */}
        {isMobileMenuOpen && (
          <div className="md:hidden fixed inset-0 bg-white bg-opacity-95 z-40 flex flex-col items-center justify-center space-y-6 animate-fade-in">
            <NavLink id="home" text="Home" onClick={scrollToSection} isMobile />
            <NavLink id="about" text="About Us" onClick={scrollToSection} isMobile />
            <NavLink id="services" text="Services" onClick={scrollToSection} isMobile />
            <NavLink id="products" text="Products" onClick={scrollToSection} isMobile />
            {/* <NavLink id="gallery" text="Gallery" onClick={scrollToSection} isMobile /> */}
            <NavLink id="testimonials" text="Testimonials" onClick={scrollToSection} isMobile />
            <NavLink id="contact" text="Contact" onClick={scrollToSection} isMobile />
            <NavLink id="interest-form" text="Interest Form" onClick={scrollToSection} isMobile />
          </div>
        )}
      </nav>

      {/* Main Content Sections */}
      <main className="pt-20"> {/* Padding to account for fixed navbar */}
        <HeroSection scrollToContact={() => scrollToSection('contact')} />
        <AboutSection />
        <ServicesSection />
        <ProductsSection />
        {/* <GallerySection /> */}
        <TestimonialsSection />
        <ContactSection />
        <InterestFormSection />
      </main>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-8 px-6 md:px-12 mt-16">
        <div className="container mx-auto text-center">
          <p className="text-sm">© {new Date().getFullYear()} Unitech Systems. All rights reserved.</p>
          <div className="flex justify-center space-x-4 mt-4">
            <a href="#" className="text-gray-400 hover:text-white transition-colors duration-300">Privacy Policy</a>
            <a href="#" className="text-gray-400 hover:text-white transition-colors duration-300">Terms of Service</a>
          </div>
        </div>
      </footer>
    </div>
  );
};

// NavLink Component for reusability
const NavLink = ({ id, text, onClick, isMobile = false }) => (
  <a
    href={`#${id}`}
    onClick={() => onClick(id)}
    className={`font-medium ${isMobile ? 'text-2xl text-gray-800 hover:text-blue-700' : 'text-gray-700 hover:text-blue-700'} transition-colors duration-300 rounded-md px-3 py-2`}
  >
    {text}
  </a>
);

// Hero Section
const HeroSection = ({ scrollToContact }) => (
  <section
    id="home"
    className="relative flex items-center justify-center min-h-[calc(100vh-80px)] bg-black"
  >
    {/* Responsive hero image */}
    <img
      src="https://i.ibb.co/99SHkBRD/Title.jpg"
      alt="Unitech Systems Hero"
      className="w-full h-auto max-h-[100vh] object-contain"
    />

    {/* CTA Button */}
    <div className="absolute inset-0 flex items-center justify-center">
      <button
        onClick={scrollToContact}
        className="bg-red-600 hover:bg-red-700 text-white font-bold py-3 px-8 rounded-full shadow-lg transition-all duration-300"
      >
        Request a Free Consultation
      </button>
    </div>
  </section>
);

// About Us Section
const AboutSection = () => (
  <section id="about" className="py-16 md:py-24 bg-gray-50">
    <div className="container mx-auto px-6 md:px-12">
      <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 text-blue-800">About Unitech Systems</h2>
      <div className="flex flex-col md:flex-row items-center md:space-x-12">
        {/* <div className="md:w-1/2 mb-8 md:mb-0">
          {/* <img
            src="https://placehold.co/600x400/007bff/ffffff?text=Our+Team"
            alt="Unitech Systems Team"
            className="rounded-xl shadow-lg w-full h-auto object-cover"
          /> 
        </div> */}
        <div className="md:w-100 text-lg leading-relaxed">
          <p className="mb-6">
With over 30 years of expertise, Unitech Systems is a trusted leader in manufacturing portable chillers like the USC 1500, USC 2000, USC 3000 3TR (3 phase), and USC 5000 5TR (3 phase). Designed for efficiency and durability, our chillers use only branded spare parts, including Kirloskar water pumps and compressors, Technique switches, Polycab cables, and SS 304 bodies with a matte finish.          </p>
          <p className="mb-6">
We also specialize in central cooling units and packaged air conditioning systems and provide sales and services for central AC packages and ductable AC systems, offering end-to-end solutions for diverse applications.          </p>
          <p>
Our products serve major Indian markets like Hyderabad, Punjab, Rajasthan, Madhya Pradesh, Kerala, Chennai, and Bangalore. Internationally, we are trusted in France, Germany, Italy, and the United States, reflecting our commitment to global quality standards.          </p>
        </div>
      </div>
    </div>
  </section>
);

// Services Section
const ServicesSection = () => (
  <section id="services" className="py-16 md:py-24 bg-white">
    <div className="container mx-auto px-6 md:px-12">
      <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 text-blue-800">Our Comprehensive Services</h2>
      <div className="space-y-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          <ServiceCard
            icon={<Lightbulb size={48} className="text-blue-600 mb-4" />}
            title="System Design & Installation"
            description="Expert design and seamless installation of custom AC and Chiller systems for new constructions and upgrades."
          />
          <ServiceCard
            icon={<CheckCircle size={48} className="text-red-600 mb-4" />}
            title="Preventative Maintenance"
            description="Regular maintenance plans to ensure peak performance, energy efficiency, and extended lifespan of your HVAC equipment."
          />
          <ServiceCard
            icon={<TrendingUp size={48} className="text-black mb-4" />}
            title="Repairs & Troubleshooting"
            description="Prompt and reliable repair services for all types of AC units and chillers, minimizing downtime and restoring comfort."
          />
        </div>
        <div className="flex flex-col lg:flex-row justify-center items-center gap-8">
          <ServiceCard
            icon={<Users size={48} className="text-blue-600 mb-6" />}
            title="Commercial & Industrial HVAC"
            description="Specialized solutions for large-scale commercial buildings, industrial facilities, and complex chiller applications."
          />
          <ServiceCard
            icon={<Clipboard size={48} className="text-red-600 mb-6" />}
            title="Energy Audits & Optimization"
            description="Comprehensive energy assessments to identify inefficiencies and implement solutions for reduced operational costs."
          />
        </div>
      </div>
    </div>
  </section>
);

// Service Card Component
const ServiceCard = ({ icon, title, description }) => (
  <div className="bg-gray-50 p-8 rounded-xl shadow-lg hover:shadow-xl transition-shadow duration-300 text-center border border-gray-100">
    <div className="flex justify-center mb-4">{icon}</div>
    <h3 className="text-xl font-semibold mb-3 text-blue-700">{title}</h3>
    <p className="text-gray-600">{description}</p>
  </div>
);

// Products Section
const ProductsSection = () => (
  <section id="products" className="py-16 md:py-24 bg-gray-50">
    <div className="container mx-auto px-6 md:px-12">
      <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 text-blue-800">Our Product Range</h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
        <ProductCard
          image="https://placehold.co/400x300/007bff/ffffff?text=Residential+AC"
          title="Residential Split AC Units"
          description="Energy-efficient and quiet split AC units perfect for homes of all sizes."
        />
        <ProductCard
          image="https://placehold.co/400x300/007bff/ffffff?text=Commercial+Chiller"
          title="Commercial Water Chillers"
          description="Robust and high-capacity chillers for commercial and industrial applications."
        />
        <ProductCard
          image="https://placehold.co/400x300/007bff/ffffff?text=Ductless+Mini-Split"
          title="Ductless Mini-Split Systems"
          description="Flexible and efficient cooling solutions for individual rooms or additions."
        />
        <ProductCard
          image="https://placehold.co/400x300/007bff/ffffff?text=Rooftop+Package"
          title="Rooftop Package Units"
          description="Integrated heating and cooling solutions for commercial buildings, easy to install."
        />
        <ProductCard
          image="https://placehold.co/400x300/007bff/ffffff?text=VRF+Systems"
          title="VRF Systems"
          description="Variable Refrigerant Flow systems offering precise temperature control and energy savings for large spaces."
        />
        <ProductCard
          image="https://placehold.co/400x300/007bff/ffffff?text=Air+Handling+Units"
          title="Air Handling Units (AHUs)"
          description="Customizable AHUs for optimal air quality and temperature management in various environments."
        />
      </div>
    </div>
  </section>
);

// Product Card Component
const ProductCard = ({ image, title, description }) => (
  <div className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow duration-300 border border-gray-100">
    <img src={image} alt={title} className="w-full h-48 object-cover" />
    <div className="p-6">
      <h3 className="text-xl font-semibold mb-2 text-blue-700">{title}</h3>
      <p className="text-gray-600 text-sm">{description}</p>
    </div>
  </div>
);

// Photo Gallery Section
// const GallerySection = () => {
//   const images = [
//     'https://placehold.co/800x600/007bff/ffffff?text=Installation+1',
//     'https://placehold.co/800x600/007bff/ffffff?text=Chiller+Project',
//     'https://placehold.co/800x600/007bff/ffffff?text=Maintenance+Work',
//     'https://placehold.co/800x600/007bff/ffffff?text=Residential+Install',
//     'https://placehold.co/800x600/007bff/ffffff?text=Commercial+HVAC',
//     'https://placehold.co/800x600/007bff/ffffff?text=Team+at+Work',
//   ];

//   return (
//     <section id="gallery" className="py-16 md:py-24 bg-white">
//       <div className="container mx-auto px-6 md:px-12">
//         <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 text-blue-800">Our Work in Pictures</h2>
//         <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
//           {images.map((src, index) => (
//             <div key={index} className="relative overflow-hidden rounded-xl shadow-lg group">
//               <img
//                 src={src}
//                 alt={`Gallery Image ${index + 1}`}
//                 className="w-full h-64 object-cover transform group-hover:scale-105 transition-transform duration-300"
//               />
//               <div className="absolute inset-0 bg-black bg-opacity-40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
//                 <p className="text-white text-lg font-semibold">View Project</p>
//               </div>
//             </div>
//           ))}
//         </div>
//       </div>
//     </section>
//   );
// };

// Testimonials Section
const TestimonialsSection = () => {
  const testimonials = [
    {
      quote: "Unitech Systems delivered an outstanding chiller installation for our facility. Their team was professional, efficient, and the system works perfectly!",
      author: "John D., Manufacturing Plant Manager",
    },
    {
      quote: "The best AC service I've ever experienced. Quick response, fair pricing, and my home is finally cool again. Highly recommend!",
      author: "Sarah L., Homeowner",
    },
    {
      quote: "We've partnered with Unitech for all our HVAC maintenance for years. Their preventative service has saved us countless headaches and costs.",
      author: "Michael R., Commercial Property Owner",
    },
  ];

  return (
    <section id="testimonials" className="py-16 md:py-24 bg-gray-50">
      <div className="container mx-auto px-6 md:px-12">
        <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 text-blue-800">What Our Clients Say</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {testimonials.map((testimonial, index) => (
            <div key={index} className="bg-white p-8 rounded-xl shadow-lg border border-gray-100">
              <p className="text-gray-700 text-lg italic mb-6">"{testimonial.quote}"</p>
              <p className="text-blue-700 font-semibold text-right">- {testimonial.author}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Contact Section
const ContactSection = () => {
  const [showModal, setShowModal] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const formActionURL = 'https://docs.google.com/forms/d/e/1FAIpQLSeU6tB9TNxK-dVx-h0oAmIRGpOJJE8yz6pJ8cQuIBvMp8HBiQ/formResponse';
  
    fetch(formActionURL, {
      method: 'POST',
      body: formData,
      mode: 'no-cors'
    })
    .then(() => {
      e.target.reset(); // Clears the form fields
      setShowModal(true);
    })
    .catch((error) => {
      console.error('Error submitting form:', error);
      // Handle error state if needed
    });
  };

  return (
    <section id="contact" className="py-16 md:py-24 bg-white">
      <div className="container mx-auto px-6 md:px-12">
        <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 text-blue-800">Get In Touch With Us</h2>
        <div className="flex flex-col lg:flex-row lg:space-x-12">
          {/* Contact Information */}
          <div className="lg:w-1/2 mb-12 lg:mb-0 bg-gray-50 p-8 rounded-xl shadow-lg border border-gray-100">
            <h3 className="text-2xl font-semibold mb-6 text-blue-700">Contact Information</h3>
            <div className="space-y-6">
              <div className="flex items-center">
                <Phone size={24} className="text-red-600 mr-4" />
                <div>
                  <p className="font-semibold">Phone:</p>
                  <a href="tel:+1234567890" className="text-gray-700 hover:text-blue-700 transition-colors duration-300">+91 9819852499, +91 9819852499</a>
                </div>
              </div>
              <div className="flex items-center">
                <Mail size={24} className="text-red-600 mr-4" />
                <div>
                  <p className="font-semibold">Email:</p>
                  <a href="mailto:info@unitechsystems.com" className="text-gray-700 hover:text-blue-700 transition-colors duration-300">u.techchiller@gmail.com</a>
                </div>
              </div>
              <div className="flex items-start">
                <MapPin size={24} className="text-red-600 mr-4 mt-1" />
                <div>
                  <p className="font-semibold">Address:</p>
                  <p className="text-gray-700">Shop Numbers 389 and 399, Khardev Nagar, Ghatla, Chembur, Mumbai.</p>
                </div>
              </div>
            </div>
            <div className="mt-8">
              <h3 className="text-2xl font-semibold mb-4 text-blue-700">Find Us on Map</h3>
              <div className="rounded-lg overflow-hidden h-64 w-full">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3771.1459398362676!2d72.90486793565402!3d19.057320036101597!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3be7c61a67716bc9%3A0x2b0534ae6c474765!2sKhardev%20Nagar%2C%20Sambhaji%20Nagar%2C%20Chembur%2C%20Mumbai%2C%20Maharashtra%2C%20India!5e0!3m2!1sen!2sus!4v1754761318048!5m2!1sen!2sus"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Unitech Systems Location"
                ></iframe>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div className="lg:w-1/2 bg-gray-50 p-8 rounded-xl shadow-lg border border-gray-100">
            <h3 className="text-2xl font-semibold mb-6 text-blue-700">Request a Call Back</h3>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">Your Name</label>
                <input
                  type="text"
                  id="name"
                  name="entry.741287015"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
                  placeholder="John Doe"
                  required
                />
              </div>
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">Your Email</label>
                <input
                  type="email"
                  id="email"
                  name="entry.990982302"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
                  placeholder="john.doe@example.com"
                  required
                />
              </div>
              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">Phone Number (Optional)</label>
                <input
                  type="tel"
                  id="phone"
                  name="entry.703087164"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
                  placeholder="1234567890"
                />
              </div>
              <div>
                <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">Message</label>
                <textarea
                  id="message"
                  name="entry.743493774"
                  rows="5"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:focus:border-blue-500 transition-all duration-200"
                  placeholder="Tell us about your project or inquiry..."
                  required
                ></textarea>
              </div>
              <button
                type="submit"
                className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-6 rounded-lg shadow-md transform hover:scale-105 transition-all duration-300 ease-in-out"
              >
                Request Call Back
              </button>
            </form>
          </div>
        </div>
      </div>

      {/* Submission Confirmation Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white p-8 rounded-xl shadow-2xl text-center max-w-sm w-full">
            <CheckCircle size={64} className="text-green-500 mx-auto mb-4" />
            <h3 className="text-2xl font-bold text-gray-800 mb-3">Thank You!</h3>
            <p className="text-gray-600 mb-6">Your request has been received. We will get back to you shortly.</p>
            <button
              onClick={() => setShowModal(false)}
              className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-6 rounded-lg transition-colors duration-300"
            >
              Close
            </button>
          </div>
        </div>
      )}
    </section>
  );
};

// Interest Form Section
const InterestFormSection = () => {
  // We only need state for the modal, not the form data
  const [showModal, setShowModal] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const formActionURL = 'https://docs.google.com/forms/d/e/1FAIpQLSfCvBdln2hWsD1dlGpssoMK2T1jfAfetV_YCuu0KC78G3rDuw/formResponse';
  
    fetch(formActionURL, {
      method: 'POST',
      body: formData,
      mode: 'no-cors'
    })
    .then(() => {
      e.target.reset(); // Clears all form fields after submission
      setShowModal(true);
    })
    .catch((error) => {
      console.error('Error submitting form:', error);
      // Handle error state if needed
    });
  };

  return (
    <section id="interest-form" className="py-16 md:py-24 bg-gray-50">
      <div className="container mx-auto px-6 md:px-12">
        <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 text-blue-800">Tell Us About Your Project</h2>
        <div className="max-w-3xl mx-auto bg-white p-8 rounded-xl shadow-lg border border-gray-100">
          <p className="text-gray-700 mb-8 text-center">
            Please fill out the form below with details about your Air Conditioning or Chiller needs, and our experts will get back to you with a tailored solution.
          </p>
          <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Personal Info */}
            <div>
              <label htmlFor="interest-name" className="block text-sm font-medium text-gray-700 mb-2">Your Name</label>
              <input
                type="text"
                id="interest-name"
                name="entry.1253745526"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
                required
              />
            </div>
            <div>
              <label htmlFor="interest-email" className="block text-sm font-medium text-gray-700 mb-2">Your Email</label>
              <input
                type="email"
                id="interest-email"
                name="entry.306806200"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
                required
              />
            </div>
            <div>
              <label htmlFor="interest-phone" className="block text-sm font-medium text-gray-700 mb-2">Phone Number</label>
              <input
                type="tel"
                id="interest-phone"
                name="entry.740452419"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
                required
              />
            </div>
            <div>
              <label htmlFor="interest-company" className="block text-sm font-medium text-gray-700 mb-2">Company Name (Optional)</label>
              <input
                type="text"
                id="interest-company"
                name="entry.960261858"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
              />
            </div>

            {/* Project Details */}
            <div>
              <label htmlFor="project-type" className="block text-sm font-medium text-gray-700 mb-2">Type of Project</label>
              <select
                id="project-type"
                name="entry.707148353"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 bg-white"
                required
              >
                <option value="">Select a type</option>
                <option value="Residential AC Installation">Residential AC Installation</option>
                <option value="Commercial HVAC System">Commercial HVAC System</option>
                <option value="Chiller System Design">Chiller System Design</option>
                <option value="Preventative Maintenance">Preventative Maintenance</option>
                <option value="Repair Services">Repair Services</option>
                <option value="Energy Audit">Energy Audit</option>
                <option value="Other">Other</option>
              </select>
            </div>
            <div>
              <label htmlFor="project-scale" className="block text-sm font-medium text-gray-700 mb-2">Project Scale</label>
              <select
                id="project-scale"
                name="entry.1491542644"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 bg-white"
                required
              >
                <option value="">Select scale</option>
                <option value="Small (Individual/Single Unit)">Small (Individual/Single Unit)</option>
                <option value="Medium (Small Business/Multiple Units)">Medium (Small Business/Multiple Units)</option>
                <option value="Large (Commercial/Industrial)">Large (Commercial/Industrial)</option>
              </select>
            </div>
            <div className="md:col-span-2">
              <label htmlFor="preferred-contact" className="block text-sm font-medium text-gray-700 mb-2">Preferred Contact Method</label>
              <div className="flex flex-wrap gap-4">
                <label className="inline-flex items-center">
                  <input
                    type="radio"
                    name="entry.162595518"
                    value="Phone"
                    className="form-radio text-blue-600 h-4 w-4"
                  />
                  <span className="ml-2 text-gray-700">Phone</span>
                </label>
                <label className="inline-flex items-center">
                  <input
                    type="radio"
                    name="entry.162595518"
                    value="Email"
                    className="form-radio text-blue-600 h-4 w-4"
                  />
                  <span className="ml-2 text-gray-700">Email</span>
                </label>
              </div>
            </div>
            <div className="md:col-span-2">
              <label htmlFor="project-details" className="block text-sm font-medium text-gray-700 mb-2">Project Details / Specific Needs</label>
              <textarea
                id="project-details"
                name="entry.1283883747"
                rows="6"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
                placeholder="Describe your requirements, existing issues, or any specific questions you have..."
              ></textarea>
            </div>

            <div className="md:col-span-2 text-center">
              <button
                type="submit"
                className="w-full md:w-auto bg-red-600 hover:bg-red-700 text-white font-bold py-3 px-10 rounded-lg shadow-md transform hover:scale-105 transition-all duration-300 ease-in-out"
              >
                Submit Interest Form
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Submission Confirmation Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white p-8 rounded-xl shadow-2xl text-center max-w-sm w-full">
            <CheckCircle size={64} className="text-green-500 mx-auto mb-4" />
            <h3 className="text-2xl font-bold text-gray-800 mb-3">Form Submitted!</h3>
            <p className="text-gray-600 mb-6">Thank you for your detailed inquiry. We will review it and contact you using your preferred method.</p>
            <button
              onClick={() => setShowModal(false)}
              className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-6 rounded-lg transition-colors duration-300"
            >
              Close
            </button>
          </div>
        </div>
      )}
    </section>
  );
};
export default App;